import * as React from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex gap-2 shrink-0 items-center justify-center whitespace-nowrap rounded text-base font-medium transition-colors focus-visible:outline-none focus-visible:ring-0 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        primary:
          'bg-primary-600 text-neutral-0 hover:bg-primary-500 active:bg-primary-700 disabled:bg-slate-100 disabled:text-slate-400',
        secondary:
          'border-solid border border-primary-200 bg-neutral-0 text-primary-600 hover:bg-primary-100 active:bg-primary-300 disabled:border-slate-300 disabled:text-slate-300',
        ghost:
          'text-primary-600 hover:bg-primary-100 active:bg-primary-300 disabled:text-slate-300',
        'maggie-primary':
          'bg-maggie-500 text-neutral-0 hover:bg-maggie-600 active:bg-maggie-800 disabled:bg-slate-100 disabled:text-slate-400',
        'maggie-secondary':
          'border-solid border border-maggie-600 bg-neutral-0 text-maggie-600 hover:bg-maggie-50 active:bg-maggie-100 disabled:border-slate-300 disabled:text-slate-300',
        'maggie-ghost':
          'text-maggie-600 hover:bg-maggie-50 active:bg-maggie-100 disabled:text-slate-300',
        'grant-primary':
          'bg-grant-600 text-neutral-0 hover:bg-grant-500 active:bg-grant-800 disabled:bg-slate-100 disabled:text-slate-400',
        'grant-secondary':
          'border-solid border border-grant-600 bg-neutral-0 text-grant-600 hover:bg-grant-50 active:bg-grant-100 disabled:border-slate-300 disabled:text-slate-300',
        'grant-ghost':
          'text-grant-600 hover:bg-grant-50 active:bg-grant-100 disabled:text-grant-300',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/80 disabled:bg-slate-100 disabled:text-slate-400',
        'outline-destructive':
          'border border-destructive text-destructive hover:bg-destructive/20',
      },
      size: {
        lg: 'h-10 px-4 py-2',
        md: 'h-9 px-4 py-2',
        sm: 'h-8 px-3 py-1.5 text-sm',
        'icon-lg': 'h-10 w-10 p-2',
        'icon-md': 'h-9 w-9 p-2',
        'icon-sm': 'h-8 w-8 p-2',
      },
      iconPosition: {
        left: 'flex-row',
        right: 'flex-row-reverse',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'lg',
      iconPosition: 'left',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      iconPosition,
      icon: Icon,
      asChild = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, iconPosition, className })
        )}
        ref={ref}
        {...props}
      >
        {Icon && (
          <Icon
            className={cn(
              !size || size === 'lg' || size === 'icon-lg'
                ? 'h-5 w-5'
                : 'h-4 w-4'
            )}
          />
        )}
        {(!size ||
          !size.includes('icon') ||
          (size.includes('icon') && !Icon)) && (
          <Slottable>{props.children}</Slottable>
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
